import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'

/**
 * Modal — primitive dialog modal reutilisable
 *
 * Pattern : backdrop noir/40 + card centree + close en haut a droite.
 * Escape key et click backdrop ferment (desactivables via props).
 *
 * Usage :
 *   <Modal
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     title="Confirmer la suppression"
 *     description="Cette action est irreversible."
 *     footer={
 *       <>
 *         <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
 *         <Button onClick={handleDelete}>Supprimer</Button>
 *       </>
 *     }
 *   >
 *     <p>Vous allez supprimer le client "{name}".</p>
 *   </Modal>
 *
 * Slots :
 *   - title       : string ou ReactNode — titre (H2)
 *   - description : string ou ReactNode — aide contextuelle optionnelle
 *   - children    : ReactNode — corps du dialog
 *   - footer      : ReactNode — zone d'actions en bas (boutons, en general)
 *
 * Variantes :
 *   - size : 'sm' (380px) | 'md' (500px, defaut) | 'lg' (720px) | 'xl' (960px)
 *   - tone : 'card' (defaut, bg blanc) | 'muted' (bg surface-low, pour read-only)
 *
 * Flags :
 *   - closeOnBackdrop  : default true  — click backdrop ferme
 *   - closeOnEsc       : default true  — ESC ferme
 *   - hideCloseButton  : default false — cache le X en haut a droite
 *
 * Z-index : fixed inset 200. Stacke sur toute la page. Pour nested modals
 * passe `zIndex` plus haut.
 */
// Mobile (<sm) : full-screen (w/h 100vw/100vh, pas de rounded, pas de max-h
// pour ne pas laisser apercevoir le contenu derriere). Desktop (>=sm) : taille
// fixee + centre + rounded + max-h 90vh comme avant. Animation : zoom-in en
// desktop, slide-in-from-bottom en mobile (style bottom sheet).
const modalVariants = cva(
  'relative shadow-lg flex flex-col w-full h-full sm:h-auto sm:max-h-[90vh] sm:rounded-xl animate-in fade-in slide-in-from-bottom-4 sm:slide-in-from-bottom-0 sm:zoom-in-95 duration-200',
  {
    variants: {
      size: {
        sm: 'sm:w-[380px] sm:max-w-[90vw]',
        md: 'sm:w-[500px] sm:max-w-[90vw]',
        lg: 'sm:w-[720px] sm:max-w-[90vw]',
        xl: 'sm:w-[960px] sm:max-w-[95vw]',
      },
      tone: {
        card:  'bg-surface-card',
        muted: 'bg-surface-low',
      },
    },
    defaultVariants: {
      size: 'md',
      tone: 'card',
    },
  }
)

export interface ModalProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof modalVariants> {
  open: boolean
  onClose: () => void
  title?: React.ReactNode
  description?: React.ReactNode
  footer?: React.ReactNode
  closeOnBackdrop?: boolean
  closeOnEsc?: boolean
  hideCloseButton?: boolean
  /** Override du z-index si besoin de stacker plusieurs modales. Default 200. */
  zIndex?: number
}

const Modal = React.forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      open,
      onClose,
      title,
      description,
      footer,
      children,
      className,
      size,
      tone,
      closeOnBackdrop = true,
      closeOnEsc = true,
      hideCloseButton = false,
      zIndex = 200,
      ...props
    },
    ref
  ) => {
    // ESC key handler
    React.useEffect(() => {
      if (!open || !closeOnEsc) return
      const handler = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose()
      }
      window.addEventListener('keydown', handler)
      return () => window.removeEventListener('keydown', handler)
    }, [open, closeOnEsc, onClose])

    if (!open) return null

    return (
      <div
        // Mobile : pas de padding (full screen). Desktop : padding 4 pour
        // l'espacement avec le bord de l'ecran.
        className="fixed inset-0 flex items-end sm:items-center justify-center sm:p-4"
        style={{ zIndex }}
      >
        <div
          className="absolute inset-0 bg-black/40"
          onClick={closeOnBackdrop ? onClose : undefined}
        />

        <div
          ref={ref}
          role="dialog"
          aria-modal="true"
          className={cn(modalVariants({ size, tone }), className)}
          {...props}
        >
          {!hideCloseButton && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Fermer"
              className="absolute top-3 right-3 w-7 h-7 rounded-md flex items-center justify-center text-ink-muted hover:bg-surface-mid hover:text-ink-secondary transition-colors cursor-pointer z-10"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}

          {(title || description) && (
            <header className="px-6 pt-6 pb-4 border-b border-ink-ghost/30 space-y-1">
              {title &&
                (typeof title === 'string' ? (
                  <h2 className="text-h3 font-semibold text-ink pr-8">{title}</h2>
                ) : (
                  title
                ))}
              {description &&
                (typeof description === 'string' ? (
                  <p className="text-body text-ink-muted">{description}</p>
                ) : (
                  description
                ))}
            </header>
          )}

          <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>

          {footer && (
            <footer className="px-6 py-4 border-t border-ink-ghost/30 flex items-center justify-end gap-2">
              {footer}
            </footer>
          )}
        </div>
      </div>
    )
  }
)
Modal.displayName = 'Modal'

export { Modal, modalVariants }
